import type { Finding } from './findings.js'
import type { Board, BoardSnapshot, Card, Column, ColumnGroup, Row, Swimlane } from './schemas.js'

/**
 * Normalized board state: entities by id plus ordered id lists per parent.
 * Components select a single entity or a single ordered list, so an edit to one
 * card leaves every other list reference untouched.
 */
export interface NormalizedBoard {
  board: Board
  groups: Record<string, ColumnGroup>
  columns: Record<string, Column>
  swimlanes: Record<string, Swimlane>
  rows: Record<string, Row>
  cards: Record<string, Card>
  /** Findings by id. Unordered: they are listed by creation time. */
  findings: Record<string, Finding>
  /** Ordered ids. */
  groupIds: string[]
  columnIds: string[]
  swimlaneIds: string[]
  rowIdsBySwimlane: Record<string, string[]>
  /** Keyed by `cellKey(rowId, columnId)`. */
  cardIdsByCell: Record<string, string[]>
}

export const EMPTY_IDS: readonly string[] = Object.freeze([])

export function cellKey(rowId: string, columnId: string): string {
  return `${rowId}|${columnId}`
}

/** Positions are authoritative; ids break ties so hydration is deterministic. */
function byPositionThenId<T extends { id: string; position: number }>(a: T, b: T): number {
  return a.position - b.position || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)
}

function indexById<T extends { id: string }>(items: readonly T[]): Record<string, T> {
  const out: Record<string, T> = {}
  for (const item of items) out[item.id] = item
  return out
}

export function normalize(snapshot: BoardSnapshot): NormalizedBoard {
  const columns = [...snapshot.columns].sort(byPositionThenId)
  const swimlanes = [...snapshot.swimlanes].sort(byPositionThenId)
  const rows = [...snapshot.rows].sort(byPositionThenId)
  const cards = [...snapshot.cards].sort(byPositionThenId)

  const rowIdsBySwimlane: Record<string, string[]> = {}
  for (const swimlane of swimlanes) rowIdsBySwimlane[swimlane.id] = []
  for (const row of rows) (rowIdsBySwimlane[row.swimlaneId] ??= []).push(row.id)

  const cardIdsByCell: Record<string, string[]> = {}
  for (const card of cards) (cardIdsByCell[cellKey(card.rowId, card.columnId)] ??= []).push(card.id)

  return {
    board: snapshot.board,
    groups: indexById(snapshot.columnGroups),
    columns: indexById(columns),
    swimlanes: indexById(swimlanes),
    rows: indexById(rows),
    cards: indexById(cards),
    findings: indexById(snapshot.findings ?? []),
    groupIds: snapshot.columnGroups.map((g) => g.id),
    columnIds: columns.map((c) => c.id),
    swimlaneIds: swimlanes.map((s) => s.id),
    rowIdsBySwimlane,
    cardIdsByCell,
  }
}

export function denormalize(state: NormalizedBoard): BoardSnapshot {
  return {
    board: state.board,
    columnGroups: state.groupIds.map((id) => state.groups[id]!),
    columns: state.columnIds.map((id) => state.columns[id]!),
    swimlanes: state.swimlaneIds.map((id) => state.swimlanes[id]!),
    rows: state.swimlaneIds.flatMap((sid) => (state.rowIdsBySwimlane[sid] ?? []).map((id) => state.rows[id]!)),
    cards: Object.values(state.cards),
    findings: Object.values(state.findings ?? {}),
  }
}

export function rowIdsFor(state: NormalizedBoard, swimlaneId: string): readonly string[] {
  return state.rowIdsBySwimlane[swimlaneId] ?? (EMPTY_IDS as string[])
}

export function cardIdsFor(state: NormalizedBoard, rowId: string, columnId: string): readonly string[] {
  return state.cardIdsByCell[cellKey(rowId, columnId)] ?? (EMPTY_IDS as string[])
}

/** Every row on the board, in swimlane then row order. */
export function orderedRowIds(state: NormalizedBoard): string[] {
  return state.swimlaneIds.flatMap((id) => state.rowIdsBySwimlane[id] ?? [])
}

export function countCardsInColumn(state: NormalizedBoard, columnId: string): number {
  let total = 0
  for (const card of Object.values(state.cards)) if (card.columnId === columnId) total += 1
  return total
}

export function countCardsInRow(state: NormalizedBoard, rowId: string): number {
  let total = 0
  for (const card of Object.values(state.cards)) if (card.rowId === rowId) total += 1
  return total
}

export function countCardsInSwimlane(state: NormalizedBoard, swimlaneId: string): number {
  const rowIds = new Set(rowIdsFor(state, swimlaneId))
  let total = 0
  for (const card of Object.values(state.cards)) if (rowIds.has(card.rowId)) total += 1
  return total
}
